/**
 * @fileoverview Global tracking kill switch.
 *
 * A single flag that turns FlowGrid off completely for a visitor: no events, no
 * identity, no session replay, no heartbeats. It outranks consent, sampling and
 * every per-feature setting — if it is set, nothing tracks.
 *
 * **Why a cookie and not localStorage.** localStorage is readable only by the
 * browser, so a server-side or edge-rendered request has no way to see it and
 * would keep recording a visitor who opted out. A cookie is attached to every
 * request to the origin, so the ingest side can honour the same flag without
 * the client having to tell it anything. That is what makes this a *global*
 * override rather than a client-side one — see the matching check in
 * flowgrid-core.
 *
 * Set it from anywhere:
 *
 * ```ts
 * fg.suppressTracking();                     // opt this visitor out
 * fg.suppressTracking(false);                // opt back in
 * localStorage.setItem('fs_ignore', 'true'); // devtools one-liner; also
 *                                            // silences the native tracker
 * document.cookie = 'fg_ignore=1; Path=/';   // or set it server-side
 * ```
 *
 * All three are equivalent: the SDK reads both forms and keeps them in step, so
 * whichever you set covers the npm SDK, the native `flowgrid.js` tracker, and
 * server-side ingest.
 */

/** Cookie form — the one the server/ingest side can see. */
export const SUPPRESSION_COOKIE = "fg_ignore";

/**
 * localStorage form, honoured for parity with the NATIVE tracker.
 *
 * `flowgrid.js` has always gated on `localStorage.getItem("fs_ignore") === "true"`
 * (seven call sites), and that is the switch actually used in practice to keep
 * internal browsing out of the data. The npm SDK ignored it, so the same
 * devtools one-liner silenced one tracker and not the other — on a site running
 * both, you would opt out and still be recorded.
 *
 * Reading it here fixes that. It is also MIRRORED into the cookie on the first
 * check (see below), because localStorage is invisible to the server: without
 * the mirror the flag would stop the browser sending, but any server-rendered
 * or edge request would still be ingested.
 */
export const SUPPRESSION_STORAGE_KEY = "fs_ignore";

/** Values that mean "off". Anything else present is treated as set. */
const FALSY = new Set(["", "0", "false", "off", "no"]);

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Truthy = set. Matches the native tracker, which only ever tests `"true"`. */
function isSet(value: string | null): boolean {
  return value !== null && !FALSY.has(value.trim().toLowerCase());
}

function readStorageFlag(): boolean {
  try {
    return isSet(localStorage.getItem(SUPPRESSION_STORAGE_KEY));
  } catch {
    // Storage disabled / partitioned — fall back to the cookie alone.
    return false;
  }
}

/**
 * Is tracking globally suppressed for this visitor?
 *
 * Checked at the single transport choke point, so every event type inherits it
 * without each feature having to remember. Never throws — a suppression check
 * that blew up would take the host page with it, and the SDK's contract is that
 * it fails silently.
 */
export function isTrackingSuppressed(): boolean {
  try {
    if (isSet(readCookie(SUPPRESSION_COOKIE))) return true;

    // Native-tracker parity. Setting `localStorage.fs_ignore` is the habit in
    // use, so promote it to the cookie the first time we see it — that is what
    // carries the opt-out to the server, which cannot read localStorage.
    if (readStorageFlag()) {
      setTrackingSuppressed(true);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Set or clear the global suppression flag.
 *
 * Written as a first-party cookie on the current origin with a one-year expiry.
 * `SameSite=Lax` so it still rides along on top-level navigations (the opt-out
 * must survive the visitor clicking through the site), and `Secure` on HTTPS.
 */
export function setTrackingSuppressed(suppressed = true): void {
  try {
    if (typeof document === "undefined") return;
    const secure = location.protocol === "https:" ? "; Secure" : "";
    if (suppressed) {
      document.cookie = `${SUPPRESSION_COOKIE}=1; Path=/; Max-Age=${365 * 24 * 60 * 60}; SameSite=Lax${secure}`;
    } else {
      document.cookie = `${SUPPRESSION_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
    }
    // Keep both forms in step. Opting back in MUST clear the localStorage flag
    // too, or the next `isTrackingSuppressed()` would see it and re-write the
    // cookie — opting in would appear to do nothing.
    try {
      if (suppressed) localStorage.setItem(SUPPRESSION_STORAGE_KEY, "true");
      else localStorage.removeItem(SUPPRESSION_STORAGE_KEY);
    } catch {
      // Storage unavailable — the cookie alone still carries the flag.
    }
  } catch {
    // Cookies unavailable (sandboxed iframe, disabled) — nothing to do.
  }
}
